import React, { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { SideNavigation, getUserResources } from '@dreebit-registry/vsm-microservice-components';
import { useKeycloak } from '@react-keycloak/web';
import { jwtDecode } from 'jwt-decode';
import _ from 'lodash';
import AttributesRoutes from "./attributes";
import SystemAnnouncementListScreen from './systemAnnouncements';
import AuthenticatedIframe from '../../components/AuthenticatedIframe';
import LegacyRouteRedirect from '../../components/LegacyRouteRedirect';

const BUSCH_IOT_URL = process.env.REACT_APP_BUSCH_IOT_URL;

const SLA_REPORT_ROLES = ['globalAdmin', 'globalUser', 'regionAdmin', 'regionUser'];

// `resource` / `roles` must mirror the gating of navItems and routes below —
// shouldShowSettingsIcon uses them to hide the settings gear for users who
// cannot access any tab.
export const getConfigurationTabs = (t: (key: string) => string) => [
    { title: t('Attributes'), route: 'attributes', resource: 'installedBaseNodePropertiesCRUD' },
    { title: t('Maintenance'), route: 'maintenance', roles: ['globalAdmin', 'regionAdmin'] },
    { title: t('IoT Configuration'), route: 'iotConfiguration', roles: ['globalAdmin'] },
    { title: t('System Announcements'), route: 'systemAnnouncements', resource: 'toastMessageCRUD' },
    { title: t('Rules'), route: 'rules', roles: ['globalAdmin', 'regionAdmin'] },
    { title: t('SLA Report'), route: 'slaReport', roles: SLA_REPORT_ROLES },
];

const ConfigurationsRoutes = () => {
    const { t } = useTranslation();
    const { keycloak } = useKeycloak();
    const resources = getUserResources(keycloak.token, 'otto_installed_base');

    const roles = useMemo(() => {
        if (!keycloak.token) return [] as string[];
        try {
            const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
            return _.get(decoded, 'otto_access.roles', []) as string[];
        } catch { return [] as string[]; }
    }, [keycloak.token]);

    const isGlobalAdmin = roles.includes('globalAdmin');
    const isIoTAdmin = isGlobalAdmin || roles.includes('regionAdmin');
    const canViewSlaReport = SLA_REPORT_ROLES.some(role => roles.includes(role));

    const navItems = useMemo(() => [
        ...(resources.includes('installedBaseNodePropertiesCRUD')
            ? [{ label: t('Attributes'), to: '/configurations/attributes' }]
            : []),
        ...(isIoTAdmin
            ? [{ label: t('Maintenance'), to: '/configurations/maintenance' }]
            : []),
        ...(isGlobalAdmin
            ? [{ label: t('IoT Configuration'), to: '/configurations/iotConfiguration' }]
            : []),
        ...(resources.includes('toastMessageCRUD')
            ? [{ label: t('System Announcements'), to: '/configurations/systemAnnouncements' }]
            : []),
        ...(isIoTAdmin
            ? [{ label: t('Rules'), to: '/configurations/rules' }]
            : []),
        ...(canViewSlaReport
            ? [{ label: t('SLA Report'), to: '/configurations/slaReport' }]
            : []),
    ], [t, resources, isGlobalAdmin, isIoTAdmin, canViewSlaReport]);

    const defaultRoute = navItems[0]?.to;

    return (
        <div className="flex-row" style={{ flexGrow: 1 }}>
            <Box
                sx={{
                    width: 320,
                    borderRight: '1px solid #ccc',
                    margin: '12px 0',
                    padding: '0 12px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <SideNavigation navItems={navItems} />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    padding: '12px 0 12px 12px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Routes>
                        <Route path="/attributes/*" element={<AttributesRoutes />} />
                        {isIoTAdmin && (
                            <Route path="/maintenance" element={<AuthenticatedIframe src={`${BUSCH_IOT_URL}/pump-maintenance`} title={t('Maintenance')} />} />
                        )}
                        {isGlobalAdmin && (
                            <Route path="/iotConfiguration" element={<AuthenticatedIframe src={`${BUSCH_IOT_URL}/pump-configuration`} title={t('IoT Configuration')} />} />
                        )}
                        {resources.includes('toastMessageCRUD') && (
                            <Route path="/systemAnnouncements" element={<SystemAnnouncementListScreen />} />
                        )}
                        {isIoTAdmin && (
                            <Route path="/rules" element={<AuthenticatedIframe src={`${BUSCH_IOT_URL}/rules`} title={t('Rules')} />} />
                        )}
                        {canViewSlaReport && (
                            <Route path="/slaReport" element={<AuthenticatedIframe src={`${BUSCH_IOT_URL}/iot-asset/sla-asset-report`} title={t('SLA Report')} />} />
                        )}
                        {/* The About page moved to /profile/about — keep old deep links working */}
                        <Route path="/about" element={<Navigate to="/profile/about" replace />} />
                        {/* Renamed to camelCase. Ungated: a user without access to the target
                            falls through to its "*" route below. */}
                        <Route path="/iot-configuration" element={<LegacyRouteRedirect />} />
                        <Route path="/system-announcements" element={<LegacyRouteRedirect />} />
                        <Route path="/sla-report" element={<LegacyRouteRedirect />} />
                        {/* Unknown or inaccessible paths fall back to the user's first
                            entry. Users without any entry never get here — routes.tsx
                            only mounts /configurations/* when at least one tab is
                            accessible (shouldShowSettingsIcon). */}
                        <Route path="*" element={<Navigate to={defaultRoute ?? '/profile/about'} replace />} />
                    </Routes>
                </Box>
            </Box>
        </div>
    );
};

export default ConfigurationsRoutes;
